// ConnectionIndicator — "the app never disappears" (pen "Anslutning", J-05),
// pure. A lost connection never replaces the app: this small indicator at the
// menu's foot says so, and its popover explains what happened, what still
// works, when the next try is, and offers "Try now".
//
// Silent when nominal: online renders nothing. Offline is a danger-toned pill
// ("Can't reach the server" / "You're offline", told apart by `reason`);
// retrying is a neutral pill ("Reconnecting …"). In the menu's rail it is the
// glyph alone, with the same popover. Colour never carries the meaning alone:
// every state has its glyph and word (the label is a live region).
//
// Honest copy: there is no offline queue yet, so it never says changes are
// kept or counted; it says reading works and changes need the connection.
//
// No developer info on screen (owner, 2026-10-22; SF-14): the popover never
// shows a version or an error ID. Its one link, "Copy error details", asks
// the shell to copy them (with the time) for a report.
//
// Pure/props-driven: facts in, intents up. The one clock read is the
// sanctioned `useNow` seam inside the open popover (the retry countdown).

export type { Health, HealthReason };
export { ConnectionIndicator };

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { useNow } from "../../hooks/useNow.js";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { Popover } from "../Popover/Popover.js";
import { Spinner } from "../Spinner/Spinner.js";
import { Text } from "../Text/Text.js";
import "./ConnectionIndicator.css";

// The session-level connectivity fact, folded from read outcomes by the shell:
// a transient read failure → offline; a retry in flight → retrying; the next
// ok → online.
type Health = "online" | "offline" | "retrying";

// Why the server can't be reached: the device has no network, or the network
// is up and the server doesn't answer.
type HealthReason = "network" | "server";

type ConnectionIndicatorProps = {
    readonly health: Health;
    readonly reason: HealthReason;
    // When the pending automatic retry fires (epoch ms); null when none waits.
    readonly nextRetryAt: number | null;
    // "pill" in the full menu, "icon" in the rail.
    readonly variant: "pill" | "icon";
    readonly onRetryNow: () => void;
    // Copy the error details (error ID, version, time); absent → no link.
    readonly onCopyDetails?: () => void;
};

// The retry line, ticking while the popover is open.
const RetryLine = ({
    health,
    nextRetryAt,
}: {
    readonly health: Health;
    readonly nextRetryAt: number | null;
}): ReactNode => {
    const { t } = useLingui();
    const now = useNow(1000);
    const seconds =
        nextRetryAt === null
            ? null
            : Math.max(1, Math.ceil((nextRetryAt - now) / 1000));
    const line =
        health === "retrying" || seconds === null
            ? t`Trying again now …`
            : t`Trying again in ${plural(seconds, {
                  one: "# second",
                  other: "# seconds",
              })}`;
    return (
        <Text as="p" size="sm" tone="dim" className="bilda-connection__retry">
            <Spinner size="sm" />
            {line}
        </Text>
    );
};

const ConnectionIndicator = ({
    health,
    reason,
    nextRetryAt,
    variant,
    onRetryNow,
    onCopyDetails,
}: ConnectionIndicatorProps): ReactNode => {
    const { t } = useLingui();
    if (health === "online") return null;

    // The pill's word: what is wrong, in the fewest words.
    const label = ((): string => {
        switch (health) {
            case "retrying":
                return t`Reconnecting …`;
            case "offline":
                return reason === "network"
                    ? t`You're offline`
                    : t`Can't reach the server`;
            default:
                return exhaustSwitchCase(health);
        }
    })();
    const title =
        reason === "network"
            ? t`Your device is offline`
            : t`We can't reach the server right now`;
    const body =
        reason === "network"
            ? t`You can keep reading what's on screen. Changes need the connection, so they can't be saved until you're back online.`
            : t`The problem is on our side. You can keep reading what's on screen. Changes need the connection, so they can't be saved until we're back.`;

    const glyph =
        health === "retrying" ? (
            <Spinner size="sm" />
        ) : (
            <Icon name="wifi-off" />
        );

    const trigger = (
        <button
            type="button"
            className={`bilda-connection bilda-connection--${health} bilda-connection--${variant}`}
            aria-label={variant === "icon" ? label : undefined}
            // The rail shows the glyph alone: its name on hover (a Radix
            // tooltip can't share the popover's trigger).
            title={variant === "icon" ? label : undefined}
        >
            <span className="bilda-connection__glyph" aria-hidden="true">
                {glyph}
            </span>
            {variant === "pill" ? (
                <span
                    className="bilda-connection__label"
                    role="status"
                    aria-live="polite"
                >
                    {label}
                </span>
            ) : null}
        </button>
    );

    return (
        <Popover
            label={title}
            side={variant === "icon" ? "right" : "top"}
            align={variant === "icon" ? "end" : "start"}
            trigger={trigger}
        >
            <div className="bilda-connection__panel">
                <Text
                    as="p"
                    weight="medium"
                    className="bilda-connection__title"
                >
                    <span
                        className="bilda-connection__title-glyph"
                        aria-hidden="true"
                    >
                        <Icon name="wifi-off" />
                    </span>
                    {title}
                </Text>
                <Text as="p" size="sm" tone="dim">
                    {body}
                </Text>
                <RetryLine health={health} nextRetryAt={nextRetryAt} />
                <div className="bilda-connection__actions">
                    <Button variant="secondary" size="sm" onClick={onRetryNow}>
                        {t`Try now`}
                        <Icon name="refresh" size="sm" />
                    </Button>
                </div>
                {onCopyDetails === undefined ? null : (
                    <p className="bilda-connection__meta">
                        <button
                            type="button"
                            className="bilda-connection__copy"
                            onClick={onCopyDetails}
                        >
                            {t`Copy error details`}
                        </button>
                    </p>
                )}
            </div>
        </Popover>
    );
};
